import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { Button } from '@/shared/ui/Button';
import { clsx } from 'clsx';
import { useToast } from '@/shared/ui/Toast';
import {
  campaignsApi,
  type Deployment,
  type CampaignBrief,
  type CampaignInsight,
  type CampaignDiff,
  type ApplyPendingOptions,
  APPLY_CONFIRM_PHRASE,
  type SearchTermCategory,
} from '../api/campaignsApi';
import {
  CHANNEL_LABELS,
  ALL_CHANNELS,
  DEPLOY_STATUS_COLOR,
  DEPLOY_STATUS_LABEL,
} from '../constants';
import { GoogleSearchReview } from '../components/GoogleSearchReview';
import { GooglePMaxReview } from '../components/GooglePMaxReview';
import { MetaReview } from '../components/MetaReview';
import { PNBadge, ExtBadge } from '../components/CampaignBadges';
import { ManagementModeProvider, AutopilotGuard } from '../components/AutopilotGuard';
import type { PlatformData } from '../api/campaignsApi';
import { describeAdScheduleSlot } from '../components/types';
import type { GoogleSearchData, MetaData, AdScheduleSlot } from '../components/types';
import { useAuthContext } from '@/shared/context/useAuthContext';
import { MetricCard } from '@/shared/ui';
import { AdsTrendChart } from '../components/AdsTrendChart';
import { DayHourHeatmap } from '../components/DayHourHeatmap';
import {
  formatConversions,
  formatCurrency,
  formatNumber,
  formatPercent,
} from '@/shared/utils/format';
import { PageHeader, ConfirmDialog, Modal } from '@/shared/ui';
import { ErrorBoundary } from '@/shared/ui/ErrorBoundary';
import { campaignKeys } from '../queryKeys';
import { integrationsApi } from '@/modules/settings/api/integrationsApi';
import { integrationKeys } from '@/modules/settings/queryKeys';
import styles from './CampaignReviewPage.module.css';

const CHANNEL_TO_PROVIDER: Record<string, 'google_ads' | 'meta_ads'> = {
  google_search: 'google_ads',
  google_pmax: 'google_ads',
  meta: 'meta_ads',
};

const PROVIDER_LABEL: Record<'google_ads' | 'meta_ads', string> = {
  google_ads: 'Google Ads',
  meta_ads: 'Meta Ads',
};

const GOOGLE_ADS_SETUP_PATH = '/settings/integrations/google-ads/setup';
const AGENCY_META_SETUP_PATH = '/agency/settings/integrations';
const META_PRACTICE_USER_MESSAGE =
  'Meta Ads is connected at the agency level. Please ask your agency to enable Meta campaigns for this practice.';

type DetailTab = 'performance' | 'setup' | 'recommendations';

/**
 * One status, consistent with the Campaigns list. `deployStatus` is the
 * deployment *lifecycle* (draft → live), while `status` is whether it's actually
 * serving (active/paused). A launched-but-paused deployment is "live" in the
 * lifecycle sense but the user pauses/reads it as Paused — so once it's live we
 * show the serving status, which is what syncs from the platform.
 */
function effectiveStatusLabel(d: { deployStatus: string; status?: string }): string {
  if (d.deployStatus === 'live') {
    if (d.status === 'paused') return 'Paused';
    if (d.status === 'active') return 'Active';
  }
  return DEPLOY_STATUS_LABEL[d.deployStatus] ?? d.deployStatus;
}
function effectiveStatusColor(d: { deployStatus: string; status?: string }): string {
  if (d.deployStatus === 'live' && d.status === 'paused') return 'var(--gray-500)';
  return DEPLOY_STATUS_COLOR[d.deployStatus] ?? 'var(--gray-500)';
}

/**
 * Overview field values for an imported (external) campaign. Its brief is a
 * lossy snapshot, so the Setup → Overview reads directly from the per-channel
 * platformData — the source of truth — instead. Fields a Google Search or Meta
 * import genuinely has no equivalent for (e.g. a discrete offer/hook) stay
 * blank rather than being fabricated.
 */
interface ExternalOverview {
  offer: string;
  cta: string;
  keyMessages: string;
  targetAudience: string;
  geo: string;
  budget: string;
  landingUrl: string;
  /** Day-parting lines, one per slot; empty when the campaign runs continuously. */
  adSchedule: string;
  /** AI Max for Search: 'On'/'Off', or '' when the campaign doesn't carry it. */
  aiMax: string;
  /** AI Max sub-toggles, formatted for a single card; '' when not carried. */
  aiMaxAssets: string;
  /** Demographic targeting summary, one dimension per line; '' when unrestricted. */
  demographics: string;
}

function fmtDailyBudget(n: number | undefined): string {
  if (typeof n !== 'number' || !(n > 0)) return '';
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}/day`;
}

function deriveExternalOverview(
  channel: string | null | undefined,
  data: PlatformData | null | undefined,
): ExternalOverview {
  const blank: ExternalOverview = {
    offer: '',
    cta: '',
    keyMessages: '',
    targetAudience: '',
    geo: '',
    budget: '',
    landingUrl: '',
    adSchedule: '',
    aiMax: '',
    aiMaxAssets: '',
    demographics: '',
  };
  if (!data) return blank;

  const fmtSchedule = (slots: AdScheduleSlot[] | undefined): string =>
    (slots ?? []).map(describeAdScheduleSlot).join('\n');

  if (channel === 'meta') {
    const d = data as MetaData;
    const ad = d.ads?.[0];
    const genders = d.targeting?.genders ?? [];
    const showsAllGenders = genders.length === 0 || genders.some((g) => /all/i.test(g));
    const audienceParts = [
      d.targeting ? `Age ${d.targeting.ageMin}–${d.targeting.ageMax}` : '',
      showsAllGenders ? 'All genders' : genders.join(', '),
    ].filter(Boolean);
    return {
      ...blank,
      cta: ad?.cta ?? '',
      keyMessages: (ad?.primaryText ?? []).filter(Boolean).join('\n'),
      targetAudience: audienceParts.join(' · '),
      budget: fmtDailyBudget(d.dailyBudget),
      adSchedule: fmtSchedule(d.adSchedule),
    };
  }

  // google_search (and other Google channels carrying the same core fields)
  const d = data as GoogleSearchData;
  return {
    ...blank,
    keyMessages: (d.callouts ?? []).filter(Boolean).join('\n'),
    targetAudience: d.audienceSummary ?? '',
    geo: (d.zipCodes ?? []).join(', '),
    budget: fmtDailyBudget(d.dailyBudget),
    landingUrl: d.finalUrl ?? '',
    adSchedule: fmtSchedule(d.adSchedule),
    aiMax: d.aiMax === undefined ? '' : d.aiMax ? 'On' : 'Off',
    aiMaxAssets: [
      d.aiMaxTextCustomization === undefined
        ? ''
        : `Text customization: ${d.aiMaxTextCustomization ? 'On' : 'Off'}`,
      d.aiMaxFinalUrlExpansion === undefined
        ? ''
        : `Final URL expansion: ${d.aiMaxFinalUrlExpansion ? 'On' : 'Off'}`,
    ]
      .filter(Boolean)
      .join('\n'),
    demographics: [
      d.demographics?.ageRanges?.length ? `Age: ${d.demographics.ageRanges.join(', ')}` : '',
      d.demographics?.genders?.length ? `Gender: ${d.demographics.genders.join(', ')}` : '',
      d.demographics?.householdIncomes?.length
        ? `Household income: ${d.demographics.householdIncomes.join(', ')}`
        : '',
    ]
      .filter(Boolean)
      .join('\n'),
  };
}

/**
 * Ads v9 Campaign Detail dashboard. Replaces the old single-page review form
 * with a 3-tab dashboard:
 *   - Performance (default landing): per-campaign metrics + trend
 *   - Setup: full campaign brief + per-platform deployment forms (the prior
 *     review form moves here)
 *   - Recommendations: per-campaign optimization recommendations
 */
export function CampaignReviewPage() {
  const { practiceId, campaignId } = useParams<{ practiceId?: string; campaignId: string }>();
  const { user } = useAuthContext();
  const isAgencyUser = !!user?.isAgency;
  const navigate = useNavigate();
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [detailTab, setDetailTab] = useState<DetailTab>('performance');
  // Setup tab also has its own sub-tabs (Overview / per-platform deployment).
  const [setupTab, setSetupTab] = useState<string>('overview');
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [confirmDeleteCampaign, setConfirmDeleteCampaign] = useState(false);
  // Nuclear force-delete of a launched-but-paused campaign (typed confirmation).
  const [forceDeleteOpen, setForceDeleteOpen] = useState(false);
  const [forceDeleteText, setForceDeleteText] = useState('');
  // The deployment whose launch is being confirmed (Live vs Paused choice).
  // The live deployment whose resume is being confirmed (resume resumes spend).
  const [resumeConfirmId, setResumeConfirmId] = useState<string | null>(null);
  const [launchChoice, setLaunchChoice] = useState<{
    deploymentId: string;
    channel: string;
  } | null>(null);

  // Where "back" returns to. When the user arrived from a specific page (Overview,
  // Performance, …), that page passes its own path — including its selected date
  // range in the query string — as navigation state, so we return them exactly
  // where they were. Otherwise fall back to the campaigns list.
  const location = useLocation();
  const navState = (location.state ?? null) as { from?: string; fromLabel?: string } | null;
  const defaultBackPath = practiceId ? `/agency/practices/${practiceId}` : '/campaigns/list';
  const backPath = navState?.from ?? defaultBackPath;
  const backLabel = navState?.fromLabel ?? 'campaigns';

  const query = useQuery({
    queryKey: campaignKeys.detail(practiceId ?? 'me', campaignId!),
    queryFn: () => campaignsApi.get(campaignId!, practiceId),
    refetchInterval: (q) => {
      const status = q.state.data?.data?.status;
      return status === 'generating' || status === 'launching' ? 3000 : false;
    },
  });

  const campaign = query.data?.data;
  const campaignLocationId = campaign?.location?.id ?? null;

  const locationConfigQuery = useQuery({
    queryKey: campaignKeys.locationAdsConfig(campaignLocationId ?? ''),
    queryFn: () => campaignsApi.autopilot.getConfig(campaignLocationId!),
    enabled: !!campaignLocationId,
  });

  const { data: googleStatus } = useQuery({
    queryKey: integrationKeys.googleAdsStatus(campaignLocationId ?? ''),
    queryFn: () => integrationsApi.googleAds.getStatus().then((r) => r.data),
    enabled: !!campaignLocationId,
    staleTime: 60_000,
  });
  const { data: metaStatus } = useQuery({
    queryKey: integrationKeys.metaAdsStatus(campaignLocationId ?? ''),
    queryFn: () =>
      integrationsApi.metaAds.getStatus(campaignLocationId ?? undefined).then((r) => r.data),
    enabled: !!campaignLocationId,
    staleTime: 60_000,
  });
  const providerConnected: Record<'google_ads' | 'meta_ads', boolean> = {
    google_ads: !!(googleStatus?.connected && googleStatus.status === 'connected'),
    meta_ads: !!(metaStatus?.connected && metaStatus.status === 'connected'),
  };
  const deployments = campaign?.campaigns ?? [];
  const brief = (campaign?.brief ?? {}) as CampaignBrief & { _rationale?: string };
  const isGenerating = campaign?.status === 'generating';
  const defaultLandingUrl =
    campaign?.location?.website ?? campaign?.location?.practice?.website ?? '';

  // Mutations (unchanged from prior version)
  const generateMutation = useMutation({
    mutationFn: () => campaignsApi.generate(campaignId!, practiceId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: campaignKeys.detail(practiceId ?? 'me', campaignId!),
      }),
    onError: (err: Error) =>
      showToast(
        (err as AxiosError<{ error?: { message?: string } }>)?.response?.data?.error?.message ??
          'Generation failed',
        'error',
      ),
  });

  const updateMutation = useMutation({
    mutationFn: (payload: { brief: Partial<CampaignBrief> }) =>
      campaignsApi.update(campaignId!, payload, practiceId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: campaignKeys.detail(practiceId ?? 'me', campaignId!),
      }),
  });

  const approveMutation = useMutation({
    mutationFn: (deploymentId: string) =>
      campaignsApi.approve(campaignId!, deploymentId, practiceId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: campaignKeys.detail(practiceId ?? 'me', campaignId!),
      }),
    onError: () => showToast('Failed to approve', 'error'),
  });

  const launchMutation = useMutation({
    mutationFn: ({
      deploymentId,
      launchStatus,
    }: {
      deploymentId: string;
      channel: string;
      launchStatus: 'ENABLED' | 'PAUSED';
    }) => campaignsApi.launch(campaignId!, deploymentId, practiceId, launchStatus),
    onSuccess: (_data, { channel, launchStatus }) => {
      const provider = CHANNEL_TO_PROVIDER[channel];
      const providerLabel = provider ? PROVIDER_LABEL[provider] : 'ads platform';
      showToast(
        launchStatus === 'ENABLED'
          ? `Campaign launched and live on ${providerLabel}`
          : `Campaign created paused on ${providerLabel} — enable it there when ready`,
        'success',
      );
      queryClient.invalidateQueries({
        queryKey: campaignKeys.detail(practiceId ?? 'me', campaignId!),
      });
    },
    onError: (err: Error) => {
      showToast(err?.message ?? 'Launch failed', 'error');
      // Refetch so the deployment reflects its real state (failed) and the stored
      // reason surfaces on the banner, instead of staying on stale "Approved".
      queryClient.invalidateQueries({
        queryKey: campaignKeys.detail(practiceId ?? 'me', campaignId!),
      });
    },
  });

  const servingStatusMutation = useMutation({
    mutationFn: ({ deploymentId, target }: { deploymentId: string; target: 'active' | 'paused' }) =>
      campaignsApi.setServingStatus(
        campaignId!,
        deploymentId,
        target,
        practiceId,
        target === 'active' ? true : undefined,
      ),
    onSuccess: (_data, { target }) => {
      showToast(target === 'active' ? 'Campaign resumed' : 'Campaign paused', 'success');
      queryClient.invalidateQueries({
        queryKey: campaignKeys.detail(practiceId ?? 'me', campaignId!),
      });
    },
    onError: (err: Error) => showToast(err?.message ?? 'Could not change status', 'error'),
  });

  const discardPendingMutation = useMutation({
    mutationFn: (deploymentId: string) =>
      campaignsApi.discardPendingUpdate(campaignId!, deploymentId, practiceId),
    onSuccess: () => {
      showToast('Pending changes discarded', 'success');
      queryClient.invalidateQueries({
        queryKey: campaignKeys.detail(practiceId ?? 'me', campaignId!),
      });
    },
    onError: (err: Error) => showToast(err?.message ?? 'Could not discard the changes', 'error'),
  });

  const applyPendingMutation = useMutation({
    mutationFn: ({ deploymentId, opts }: { deploymentId: string; opts: ApplyPendingOptions }) =>
      campaignsApi.applyPendingUpdate(campaignId!, deploymentId, opts, practiceId),
    onSuccess: (res) => {
      const r = res.data;
      const parts = [
        r.applied.length ? `${r.applied.length} updated on Google` : null,
        r.manual.length ? `${r.manual.length} need manual handling` : null,
        // Name what failed and why, so it can be fixed without digging.
        r.failed.length
          ? `Failed — ${r.failed.map((f) => `${f.label}: ${f.error}`).join('; ')}`
          : null,
      ].filter(Boolean);
      showToast(parts.join(' · ') || 'Nothing to update', r.failed.length ? 'error' : 'success');
      queryClient.invalidateQueries({
        queryKey: campaignKeys.detail(practiceId ?? 'me', campaignId!),
      });
    },
    onError: (err: Error) =>
      showToast(
        (err as AxiosError<{ error?: { message?: string } }>)?.response?.data?.error?.message ??
          err?.message ??
          'Could not update Google',
        'error',
      ),
  });

  const updateDeploymentMutation = useMutation({
    mutationFn: ({
      deploymentId,
      platformData,
    }: {
      deploymentId: string;
      platformData: PlatformData;
    }) => campaignsApi.updateDeployment(campaignId!, deploymentId, platformData, practiceId),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: campaignKeys.detail(practiceId ?? 'me', campaignId!),
      }),
    onError: (err: Error) =>
      showToast(
        (err as AxiosError<{ error?: { message?: string } }>)?.response?.data?.error?.message ??
          'Failed to save changes',
        'error',
      ),
  });

  const deleteDeploymentMutation = useMutation({
    mutationFn: (deploymentId: string) =>
      campaignsApi.deleteDeployment(
        campaignId!,
        deploymentId,
        campaign?.locationId ?? '',
        practiceId,
      ),
    onSuccess: () => {
      setSetupTab('overview');
      queryClient.invalidateQueries({
        queryKey: campaignKeys.detail(practiceId ?? 'me', campaignId!),
      });
      showToast('Deployment deleted', 'success');
    },
    onError: (err: Error) =>
      showToast(
        (err as AxiosError<{ error?: { message?: string } }>)?.response?.data?.error?.message ??
          'Failed to delete deployment',
        'error',
      ),
  });

  const deleteCampaignMutation = useMutation({
    mutationFn: (confirmPhrase?: string) =>
      campaignsApi.deleteCampaign(campaignId!, practiceId, confirmPhrase),
    onSuccess: () => {
      showToast('Campaign deleted', 'success');
      navigate(backPath);
    },
    onError: (err: Error) =>
      showToast(
        (err as AxiosError<{ error?: { message?: string } }>)?.response?.data?.error?.message ??
          'Failed to delete campaign',
        'error',
      ),
  });

  // Chain brief saves so that rapid toggles + a subsequent Generate click
  // always see the latest platforms in the DB. Without this, mutate() is
  // fire-and-forget and Generate can race ahead of a pending PATCH,
  // generating against stale brief.platforms.
  const pendingSaveRef = useRef<Promise<unknown>>(Promise.resolve());
  const saveBriefField = (field: string, value: CampaignBrief[keyof CampaignBrief] | undefined) => {
    pendingSaveRef.current = pendingSaveRef.current
      .catch(() => undefined)
      .then(() => updateMutation.mutateAsync({ brief: { ...brief, [field]: value } }));
  };

  if (query.isLoading) {
    return (
      <div className={styles.page}>
        <div className={styles.loadingState}>Loading campaign…</div>
      </div>
    );
  }

  if (!campaign) return null;

  const managementMode = locationConfigQuery.data?.data?.campaignApprovalPreference ?? 'autopilot';
  // Agency users bypass autopilot restrictions — they can edit/approve in any mode.
  const isAutopilot = managementMode === 'autopilot' && !isAgencyUser;
  const isExternal = (campaign as { source?: string }).source === 'external';
  // Imported campaigns show the Overview from their per-channel platformData
  // (the source of truth), since their brief is a lossy snapshot.
  const primaryDeployment = deployments[0] ? withPendingVersion(deployments[0]) : undefined;
  const externalOverview = isExternal
    ? deriveExternalOverview(
        primaryDeployment?.channel ?? primaryDeployment?.platform,
        primaryDeployment?.platformData,
      )
    : null;
  const platforms = (brief.platforms ?? []) as string[];
  const liveChannels = new Set(
    deployments
      .filter((d) => d.deployStatus === 'live' || d.deployStatus === 'launching')
      .map((d) => d.channel ?? d.platform),
  );
  const regenerableChannels = platforms.filter((p) => !liveChannels.has(p));
  const existingNonLiveDeployments = deployments.filter(
    (d) => d.deployStatus !== 'live' && d.deployStatus !== 'launching',
  );
  const setupTabIndex = setupTab === 'overview' ? -1 : parseInt(setupTab, 10);
  const activeDeployment = deployments[setupTabIndex] as Deployment | undefined;
  // Removed directly on the platform (e.g. in Google Ads): kept on record but
  // read-only — nothing can launch, push, or edit it.
  const activeRemoved = !!activeDeployment?.removedFromPlatformAt;

  // Aggregate per-campaign performance from deployments
  const totalSpend = deployments.reduce((s, d) => s + (d.spend ?? 0), 0);
  const totalConv = deployments.reduce((s, d) => s + (d.conversions ?? 0), 0);
  const totalImpr = deployments.reduce((s, d) => s + (d.impressions ?? 0), 0);
  const totalClicks = deployments.reduce((s, d) => s + (d.clicks ?? 0), 0);
  const cpa = totalConv > 0 ? totalSpend / totalConv : 0;
  const ctr = totalImpr > 0 ? totalClicks / totalImpr : 0;

  return (
    <ManagementModeProvider mode={managementMode} isAgency={isAgencyUser}>
      <div className={styles.page}>
        {/* Header */}
        <div className={styles.header}>
          <button className={styles.back} onClick={() => navigate(backPath)}>
            ← Back to {backLabel}
          </button>
          <PageHeader
            actions={
              campaign.deletable ? (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setConfirmDeleteCampaign(true)}
                >
                  Delete campaign
                </Button>
              ) : campaign.forceDeletable ? (
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => {
                    setForceDeleteText('');
                    setForceDeleteOpen(true);
                  }}
                >
                  Delete campaign
                </Button>
              ) : undefined
            }
            title={
              <>
                {campaign.name} {isExternal ? <ExtBadge /> : <PNBadge />}
              </>
            }
            subtitle={
              <>
                {campaign.objective
                  ? campaign.objective.charAt(0).toUpperCase() + campaign.objective.slice(1)
                  : null}
                {brief.monthlyBudget
                  ? ` · $${Math.round(brief.monthlyBudget).toLocaleString()}/mo`
                  : ''}
                {campaign.generatedAt &&
                  ` · Generated ${new Date(campaign.generatedAt).toLocaleDateString()}`}
              </>
            }
          />
        </div>

        {isGenerating && (
          <div className={styles.generatingBanner}>
            <span className={styles.generatingSpinner} />
            <span>Claude is writing your ad copy — this takes about 60 seconds…</span>
          </div>
        )}

        {(brief as { importedFrom?: string }).importedFrom === 'ad-config-file' && (
          <div className={styles.importedNote}>
            <strong>Imported from an ad config file.</strong> When launched, this is created{' '}
            <strong>paused</strong> on Google — nothing spends until it is enabled there. Budget,
            bid strategy, keywords, ad copy, sitelinks, callouts, negatives, geo and language
            settings are sent. Not sent yet: headline pinning, ad schedule, and call-asset phone
            numbers.
          </div>
        )}

        {/* AI Strategy Rationale (always visible) */}
        {brief._rationale && (
          <div className={styles.rationale}>
            <span className={styles.rationaleLabel}>AI Strategy Rationale</span>
            <p className={styles.rationaleText}>{brief._rationale}</p>
          </div>
        )}

        {/* Detail tabs */}
        <div className={styles.tabs}>
          {(['performance', 'setup', 'recommendations'] as DetailTab[]).map((t) => (
            <button
              key={t}
              className={`${styles.tab} ${detailTab === t ? styles.tabActive : ''}`}
              onClick={() => setDetailTab(t)}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>

        {/* ─── Performance tab ─────────────────────────────────────── */}
        {detailTab === 'performance' && (
          <CampaignPerformanceTab
            campaignId={campaignId!}
            totalSpend={totalSpend}
            totalImpr={totalImpr}
            totalClicks={totalClicks}
            totalConv={totalConv}
            ctr={ctr}
            cpa={cpa}
          />
        )}

        {/* ─── Setup tab ───────────────────────────────────────────── */}
        {detailTab === 'setup' && (
          <div className={styles.tabContent}>
            {/* Sub-tabs for Setup: Overview + per-platform deployments */}
            <div className={styles.subTabs}>
              <button
                className={`${styles.subTab} ${setupTab === 'overview' ? styles.subTabActive : ''}`}
                onClick={() => setSetupTab('overview')}
              >
                Overview
              </button>
              {deployments.map((d, i) => (
                <button
                  key={d.id}
                  className={`${styles.subTab} ${setupTab === String(i) ? styles.subTabActive : ''}`}
                  onClick={() => setSetupTab(String(i))}
                >
                  {CHANNEL_LABELS[d.channel ?? d.platform] ?? d.channel ?? d.platform}
                  <span
                    className={styles.tabStatus}
                    style={{ background: effectiveStatusColor(d) }}
                    title={effectiveStatusLabel(d)}
                  />
                </button>
              ))}
            </div>

            {setupTab === 'overview' && (
              <div className={styles.overviewContent}>
                <div className={styles.briefFields}>
                  <InlineCard
                    label="Offer / Hook"
                    value={externalOverview ? externalOverview.offer : brief.offer}
                    onSave={(v) => saveBriefField('offer', v)}
                    readOnly={isExternal || isAutopilot}
                  />
                  <InlineCard
                    label="CTA"
                    value={externalOverview ? externalOverview.cta : brief.cta}
                    onSave={(v) => saveBriefField('cta', v)}
                    readOnly={isExternal || isAutopilot}
                  />
                  <InlineCard
                    label="Key Messages"
                    value={
                      externalOverview
                        ? externalOverview.keyMessages
                        : (brief.keyMessages ?? []).filter(Boolean).join('\n')
                    }
                    onSave={(v) =>
                      saveBriefField(
                        'keyMessages',
                        v
                          .split('\n')
                          .map((s) => s.trim())
                          .filter(Boolean),
                      )
                    }
                    multiline
                    readOnly={isExternal || isAutopilot}
                  />
                  <InlineCard
                    label="Audience"
                    value={
                      externalOverview ? externalOverview.targetAudience : brief.targetAudience
                    }
                    onSave={(v) => saveBriefField('targetAudience', v)}
                    readOnly={isExternal || isAutopilot}
                  />
                  <InlineCard
                    label="Zip Codes"
                    value={externalOverview ? externalOverview.geo : brief.geo}
                    onSave={(v) => saveBriefField('geo', v)}
                    readOnly={isExternal || isAutopilot}
                  />
                  <InlineCard
                    label="Budget"
                    value={
                      externalOverview
                        ? externalOverview.budget
                        : brief.monthlyBudget
                          ? `$${Math.round(brief.monthlyBudget).toLocaleString()}/mo`
                          : ''
                    }
                    onSave={(v) =>
                      saveBriefField('monthlyBudget', parseFloat(v.replace(/[^0-9.]/g, '')) || 1500)
                    }
                    readOnly={isExternal || isAutopilot}
                  />
                  <InlineCard
                    label="Landing URL"
                    value={
                      externalOverview
                        ? externalOverview.landingUrl || defaultLandingUrl
                        : brief.landingUrl || defaultLandingUrl
                    }
                    onSave={(v) => saveBriefField('landingUrl', v || undefined)}
                    placeholder={defaultLandingUrl || 'https://yourpractice.com'}
                    readOnly={isExternal || isAutopilot}
                  />
                  {externalOverview && externalOverview.adSchedule && (
                    <InlineCard
                      label="Ad schedule"
                      value={externalOverview.adSchedule}
                      onSave={() => {}}
                      multiline
                      readOnly
                    />
                  )}
                  {externalOverview && externalOverview.aiMax && (
                    <InlineCard
                      label="AI Max"
                      value={externalOverview.aiMax}
                      onSave={() => {}}
                      readOnly
                    />
                  )}
                  {externalOverview && externalOverview.aiMaxAssets && (
                    <InlineCard
                      label="AI Max assets"
                      value={externalOverview.aiMaxAssets}
                      onSave={() => {}}
                      multiline
                      readOnly
                    />
                  )}
                  {externalOverview && externalOverview.demographics && (
                    <InlineCard
                      label="Demographics"
                      value={externalOverview.demographics}
                      onSave={() => {}}
                      multiline
                      readOnly
                    />
                  )}
                  {!isExternal && (
                    <div className={styles.fieldCard}>
                      <span className={styles.fieldLabel}>Platforms</span>
                      <div className={styles.platformPicker}>
                        {isAutopilot
                          ? platforms.map((v) => {
                              const ch = ALL_CHANNELS.find((c) => c.value === v);
                              return (
                                <span
                                  key={v}
                                  className={`${styles.platformChip} ${styles.platformChipActive}`}
                                >
                                  {ch?.label ?? v}
                                </span>
                              );
                            })
                          : ALL_CHANNELS.map((p) => {
                              const isLive = liveChannels.has(p.value);
                              return (
                                <button
                                  key={p.value}
                                  type="button"
                                  className={`${styles.platformChip} ${platforms.includes(p.value) ? styles.platformChipActive : ''} ${!p.available || isLive ? styles.platformChipDisabled : ''}`}
                                  onClick={() => {
                                    if (!p.available || isLive) return;
                                    const next = platforms.includes(p.value)
                                      ? platforms.filter((x) => x !== p.value)
                                      : [...platforms, p.value];
                                    saveBriefField('platforms', next);
                                  }}
                                  disabled={!p.available || isLive}
                                >
                                  {p.label}
                                  {!p.available && <span className={styles.comingSoon}>soon</span>}
                                  {isLive && <span className={styles.liveTag}>Live</span>}
                                </button>
                              );
                            })}
                      </div>
                    </div>
                  )}
                </div>

                {!isExternal && !isAutopilot && (
                  <div className={styles.generateBar}>
                    <Button
                      variant={deployments.length > 0 ? 'secondary' : 'primary'}
                      onClick={async () => {
                        // Wait for any in-flight brief save (e.g. just-toggled
                        // platforms) to land before generating — otherwise the
                        // backend reads stale brief.platforms.
                        await pendingSaveRef.current.catch(() => undefined);
                        if (existingNonLiveDeployments.length > 0) {
                          setConfirmRegenerate(true);
                          return;
                        }
                        generateMutation.mutate();
                      }}
                      isLoading={generateMutation.isPending || isGenerating}
                      disabled={regenerableChannels.length === 0}
                    >
                      {isGenerating
                        ? 'Generating…'
                        : deployments.length > 0
                          ? 'Regenerate Ad Copy'
                          : 'Generate Ad Copy'}
                    </Button>
                    {!deployments.length && (
                      <span className={styles.generateHint}>
                        AI will create ad copy for each selected platform
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}

            {activeDeployment && activeRemoved && (
              <div className={styles.removedNotice}>
                <strong>
                  Removed on {CHANNEL_LABELS[activeDeployment.channel ?? ''] ?? 'the platform'}.
                </strong>{' '}
                This campaign was removed directly on the ad platform, so it’s kept here for the
                record but is read-only — it can’t be launched, edited, or pushed. Use “Delete
                campaign” to remove it from PracticeNova too.
              </div>
            )}

            {activeDeployment && (
              <div className={styles.reviewPanel}>
                <AutopilotGuard>
                  <div
                    className={`${styles.statusBanner} ${styles[`statusBanner_${activeDeployment.deployStatus}`] ?? ''}`}
                  >
                    <span
                      className={styles.statusBannerDot}
                      style={{ background: effectiveStatusColor(activeDeployment) }}
                    />
                    <span className={styles.statusBannerText}>
                      {effectiveStatusLabel(activeDeployment)}
                      {activeDeployment.deployStatus === 'live' && activeDeployment.launchedAt && (
                        <>
                          {' '}
                          · Launched {new Date(activeDeployment.launchedAt).toLocaleDateString()}
                        </>
                      )}
                    </span>
                    {activeDeployment.errorMessage && (
                      <span className={styles.statusBannerError}>
                        {activeDeployment.errorMessage}
                      </span>
                    )}
                    {activeDeployment.deployStatus === 'ready' && (
                      <Button
                        size="sm"
                        onClick={() => approveMutation.mutate(activeDeployment.id)}
                        isLoading={approveMutation.isPending}
                      >
                        Approve
                      </Button>
                    )}
                    {(() => {
                      const channel = activeDeployment.channel ?? activeDeployment.platform;
                      const provider = CHANNEL_TO_PROVIDER[channel];
                      const needsConnect = provider && !providerConnected[provider];
                      const channelLabel = CHANNEL_LABELS[channel];

                      if (
                        activeDeployment.deployStatus === 'approved' ||
                        activeDeployment.deployStatus === 'failed'
                      ) {
                        if (needsConnect) {
                          const isMetaPracticeUser = provider === 'meta_ads' && !isAgencyUser;
                          const label = isMetaPracticeUser
                            ? 'Meta Ads not connected — ask your agency'
                            : `Connect ${PROVIDER_LABEL[provider]} to launch`;
                          const onConnect = () => {
                            if (isMetaPracticeUser) {
                              showToast(META_PRACTICE_USER_MESSAGE, 'info');
                              return;
                            }
                            navigate(
                              provider === 'meta_ads'
                                ? AGENCY_META_SETUP_PATH
                                : GOOGLE_ADS_SETUP_PATH,
                            );
                          };
                          return (
                            <Button
                              size="sm"
                              variant={isMetaPracticeUser ? 'secondary' : undefined}
                              onClick={onConnect}
                            >
                              {label}
                            </Button>
                          );
                        }
                        return (
                          <Button
                            size="sm"
                            variant={
                              activeDeployment.deployStatus === 'failed' ? 'secondary' : undefined
                            }
                            onClick={() =>
                              setLaunchChoice({
                                deploymentId: activeDeployment.id,
                                channel,
                              })
                            }
                            isLoading={launchMutation.isPending}
                          >
                            {activeDeployment.deployStatus === 'failed'
                              ? 'Retry Launch'
                              : `Launch to ${channelLabel}`}
                          </Button>
                        );
                      }
                      return null;
                    })()}
                    {activeDeployment.deployStatus === 'live' &&
                      !activeRemoved &&
                      (activeDeployment.status === 'active' ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() =>
                            servingStatusMutation.mutate({
                              deploymentId: activeDeployment.id,
                              target: 'paused',
                            })
                          }
                          isLoading={servingStatusMutation.isPending}
                        >
                          Pause
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => setResumeConfirmId(activeDeployment.id)}
                          isLoading={servingStatusMutation.isPending}
                        >
                          Resume
                        </Button>
                      ))}
                    {activeDeployment.deployStatus !== 'live' &&
                      activeDeployment.deployStatus !== 'launching' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setConfirmDeleteId(activeDeployment.id)}
                          isLoading={deleteDeploymentMutation.isPending}
                        >
                          Delete
                        </Button>
                      )}
                  </div>
                </AutopilotGuard>

                {activeDeployment.pendingDiff?.hasChanges && (
                  <PendingChangesBanner
                    diff={activeDeployment.pendingDiff}
                    onDiscard={() => discardPendingMutation.mutate(activeDeployment.id)}
                    discarding={discardPendingMutation.isPending}
                    onPush={(opts) =>
                      applyPendingMutation.mutate({ deploymentId: activeDeployment.id, opts })
                    }
                    pushing={applyPendingMutation.isPending}
                    serving={activeDeployment.status === 'active'}
                    platformRemoved={activeRemoved}
                  />
                )}

                <ErrorBoundary
                  label="deployment-review"
                  fallback={() => (
                    <div className={styles.reviewErrorCard}>
                      <strong>This deployment's details couldn't be shown.</strong>
                      <p>
                        It was likely created outside PracticeNova or before the current editor, so
                        its stored shape is incomplete. Its status and budget still sync in — manage
                        the campaign directly in the ad platform.
                      </p>
                    </div>
                  )}
                >
                  {activeDeployment.channel === 'google_search' && (
                    <GoogleSearchReview
                      deployment={withPendingVersion(activeDeployment)}
                      onSave={(platformData) =>
                        updateDeploymentMutation.mutate({
                          deploymentId: activeDeployment.id,
                          platformData,
                        })
                      }
                      isSaving={updateDeploymentMutation.isPending}
                    />
                  )}
                  {activeDeployment.channel === 'google_pmax' && campaign?.location?.id && (
                    <GooglePMaxReview
                      deployment={activeDeployment}
                      campaignId={campaignId!}
                      locationId={campaign.location.id}
                      practiceId={practiceId}
                      onSave={(platformData) =>
                        updateDeploymentMutation.mutate({
                          deploymentId: activeDeployment.id,
                          platformData,
                        })
                      }
                      isSaving={updateDeploymentMutation.isPending}
                    />
                  )}
                  {activeDeployment.channel === 'meta' && campaign?.location?.id && (
                    <MetaReview
                      deployment={activeDeployment}
                      campaignId={campaignId!}
                      locationId={campaign.location.id}
                      practiceId={practiceId}
                      onSave={(platformData) =>
                        updateDeploymentMutation.mutate({
                          deploymentId: activeDeployment.id,
                          platformData,
                        })
                      }
                      isSaving={updateDeploymentMutation.isPending}
                    />
                  )}
                </ErrorBoundary>
              </div>
            )}
          </div>
        )}

        {/* ─── Recommendations tab ─────────────────────────────────── */}
        {detailTab === 'recommendations' && <CampaignRecsTab campaignId={campaignId!} />}
      </div>

      {/* Confirm dialogs */}
      <ConfirmDialog
        isOpen={confirmRegenerate}
        onClose={() => setConfirmRegenerate(false)}
        onConfirm={() => {
          setConfirmRegenerate(false);
          generateMutation.mutate();
        }}
        title="Regenerate Ad Copy"
        message={
          existingNonLiveDeployments.some((d) => d.deployStatus === 'approved')
            ? `Previously generated ad copy for ${existingNonLiveDeployments.map((d) => CHANNEL_LABELS[d.channel ?? d.platform] ?? d.channel ?? d.platform).join(', ')} will be deleted, including approved copies.`
            : `Previously generated ad copy for ${existingNonLiveDeployments.map((d) => CHANNEL_LABELS[d.channel ?? d.platform] ?? d.channel ?? d.platform).join(', ')} will be deleted.`
        }
        confirmLabel="Regenerate"
        variant="destructive"
      />
      <ConfirmDialog
        isOpen={!!confirmDeleteId}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={() => {
          if (confirmDeleteId) deleteDeploymentMutation.mutate(confirmDeleteId);
          setConfirmDeleteId(null);
        }}
        title="Delete Ad Copy"
        message="This deployment will be permanently deleted. This cannot be undone."
        confirmLabel="Delete"
        variant="destructive"
      />
      <ConfirmDialog
        isOpen={!!resumeConfirmId}
        onClose={() => setResumeConfirmId(null)}
        onConfirm={() => {
          if (resumeConfirmId)
            servingStatusMutation.mutate({ deploymentId: resumeConfirmId, target: 'active' });
          setResumeConfirmId(null);
        }}
        title="Resume campaign"
        message="This turns the campaign back on and resumes spend on the ad platform. Continue?"
        confirmLabel="Resume"
      />
      <ConfirmDialog
        isOpen={confirmDeleteCampaign}
        onClose={() => setConfirmDeleteCampaign(false)}
        onConfirm={() => {
          setConfirmDeleteCampaign(false);
          deleteCampaignMutation.mutate(undefined);
        }}
        title="Delete this campaign?"
        message="This campaign exists only in PracticeNova and has never been launched to Google or Meta, so deleting it removes it completely with no platform impact. This cannot be undone."
        confirmLabel="Delete campaign"
        variant="destructive"
        isLoading={deleteCampaignMutation.isPending}
      />
      <Modal
        isOpen={forceDeleteOpen}
        onClose={() => setForceDeleteOpen(false)}
        title="Delete this campaign from PracticeNova?"
        size="sm"
        compact
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setForceDeleteOpen(false)}
              disabled={deleteCampaignMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={
                deleteCampaignMutation.isPending ||
                forceDeleteText.trim().toUpperCase() !== 'DELETE'
              }
              isLoading={deleteCampaignMutation.isPending}
              onClick={() => {
                setForceDeleteOpen(false);
                deleteCampaignMutation.mutate('DELETE');
              }}
            >
              Delete from PracticeNova
            </Button>
          </>
        }
      >
        <div className={styles.pushDialog}>
          <div className={styles.pushWarn}>
            <strong>This deletes the campaign from PracticeNova only.</strong>
            <ul>
              <li>
                It does <strong>nothing</strong> on Google or Meta — the campaign will keep existing
                there (paused) until you delete or remove it on the platform yourself.
              </li>
              <li>
                Its PracticeNova record and reporting history are removed and can’t be recovered.
              </li>
            </ul>
          </div>
          <div className={styles.pushConfirm}>
            <label htmlFor="force-delete-phrase">
              Type <strong>DELETE</strong> to confirm.
            </label>
            <input
              id="force-delete-phrase"
              type="text"
              className={styles.pushConfirmInput}
              value={forceDeleteText}
              onChange={(e) => setForceDeleteText(e.target.value)}
              placeholder="DELETE"
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
            />
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={!!launchChoice}
        onClose={() => setLaunchChoice(null)}
        title={
          launchChoice
            ? `Launch to ${CHANNEL_LABELS[launchChoice.channel] ?? launchChoice.channel}?`
            : 'Launch'
        }
        size="xs"
        compact
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setLaunchChoice(null)}
              disabled={launchMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                if (launchChoice)
                  launchMutation.mutate({
                    deploymentId: launchChoice.deploymentId,
                    channel: launchChoice.channel,
                    launchStatus: 'PAUSED',
                  });
                setLaunchChoice(null);
              }}
              disabled={launchMutation.isPending}
            >
              Launch paused
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                if (launchChoice)
                  launchMutation.mutate({
                    deploymentId: launchChoice.deploymentId,
                    channel: launchChoice.channel,
                    launchStatus: 'ENABLED',
                  });
                setLaunchChoice(null);
              }}
              isLoading={launchMutation.isPending}
            >
              Launch live
            </Button>
          </>
        }
      >
        <div>
          <p>
            <strong>Launch live</strong> creates the campaign enabled — it starts serving and
            spending as soon as the ad platform finishes review.
          </p>
          <p>
            <strong>Launch paused</strong> creates everything on the platform but leaves it paused,
            so nothing spends until you enable it there (useful for a final check or a low-budget
            test).
          </p>
        </div>
      </Modal>
    </ManagementModeProvider>
  );
}

// ─── Pending changes from a re-imported ad-config file ──────────────────────

const PENDING_MECHANISM_LABEL: Record<string, string> = {
  live_edit: 'live edit',
  replace: 'replaces ad',
  recreate: 'needs recreate',
};

// Google Search fields the push applies automatically — mirror of the backend's
// GOOGLE_AUTO_APPLY_FIELDS (adConfig.updatePlan.ts). Everything else is shown as
// "push manually" so the operator knows what the button does and doesn't cover.
const GOOGLE_AUTO_APPLY_FIELDS = new Set<string>([
  'budget',
  'bidStrategy',
  'finalUrl',
  'ad',
  'displayPaths',
  'searchPartners',
  'displayNetwork',
  'locationTargeting',
  'status',
  'keywords',
  'negativeKeywords',
  'sitelinks',
  'callouts',
  'callAssets',
  'zipCodes',
  'languages',
  'adSchedule',
]);

/**
 * The version of a Google Search deployment to show on the Setup tab. When a
 * file has replaced PracticeNova's copy but it isn't on Google yet, that pending
 * version is what PracticeNova now holds, so it's what the page shows — the
 * banner above it says it isn't live yet.
 */
function withPendingVersion(d: Deployment): Deployment {
  if (d.channel !== 'google_search') return d;
  const pending = d.pendingUpdate?.platformData;
  return d.pendingDiff?.hasChanges && pending ? { ...d, platformData: pending } : d;
}

function PendingChangesBanner({
  diff,
  onDiscard,
  discarding,
  onPush,
  pushing,
  serving,
  platformRemoved = false,
}: {
  diff: CampaignDiff;
  onDiscard: () => void;
  discarding: boolean;
  onPush: (opts: ApplyPendingOptions) => void;
  pushing: boolean;
  /** Whether the campaign is currently serving (ENABLED) on the platform. */
  serving: boolean;
  /** Whether the campaign was removed on the platform — pushing is impossible. */
  platformRemoved?: boolean;
}) {
  const isGoogle = diff.channel === 'google_search';
  const autoCount = diff.changes.filter((c) => GOOGLE_AUTO_APPLY_FIELDS.has(c.field)).length;
  const manualCount = diff.changes.length - autoCount;
  const statusChange = diff.changes.some((c) => c.status);
  const canPush = isGoogle && autoCount > 0 && !platformRemoved;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [pauseFirst, setPauseFirst] = useState(serving);
  const [overrideDrift, setOverrideDrift] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  const phraseOk = confirmText.trim().toUpperCase() === APPLY_CONFIRM_PHRASE;
  const confirmDisabled = pushing || !phraseOk;

  const openDialog = () => {
    setPauseFirst(serving);
    setOverrideDrift(false);
    setConfirmText('');
    setDialogOpen(true);
  };

  return (
    <div className={styles.pendingBanner}>
      <div className={styles.pendingHead}>
        <div>
          <strong>
            Updated in PracticeNova — not on {diff.channel === 'meta' ? 'Meta' : 'Google'} yet
          </strong>
          <p className={styles.pendingSub}>
            The settings below are PracticeNova&rsquo;s new version from an imported file. The
            campaign on {diff.channel === 'meta' ? 'Meta' : 'Google'} is unchanged until you update
            it.
            {isGoogle
              ? ' Review what changed, then update it on Google.'
              : ' Updating Meta from here isn’t supported yet — apply these manually for now.'}
          </p>
        </div>
        <div className={styles.pendingActions}>
          {canPush && (
            <Button variant="primary" size="sm" onClick={openDialog} isLoading={pushing}>
              Update on Google
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={onDiscard} isLoading={discarding}>
            Discard
          </Button>
        </div>
      </div>
      <div className={styles.pendingChanges}>
        {diff.changes.map((c, i) => {
          const auto = GOOGLE_AUTO_APPLY_FIELDS.has(c.field);
          return (
            <div key={i} className={styles.pendingRow}>
              <span className={styles.pendingField}>{c.label}</span>
              <span className={styles.pendingSummary}>{c.summary}</span>
              <span className={styles.pendingTags}>
                <span className={clsx(styles.pendingTag, styles[`pendingMech_${c.mechanism}`])}>
                  {PENDING_MECHANISM_LABEL[c.mechanism] ?? c.mechanism}
                </span>
                {c.money && (
                  <span className={clsx(styles.pendingTag, styles.pendingMoney)}>
                    affects spend
                  </span>
                )}
                {c.status && (
                  <span className={clsx(styles.pendingTag, styles.pendingStatus)}>status</span>
                )}
                {isGoogle && !auto && (
                  <span className={clsx(styles.pendingTag, styles.pendingManual)}>
                    push manually
                  </span>
                )}
              </span>
            </div>
          );
        })}
      </div>

      <Modal
        isOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title="Update the campaign on Google?"
        size="sm"
        compact
        footer={
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setDialogOpen(false)}
              disabled={pushing}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              disabled={confirmDisabled}
              isLoading={pushing}
              onClick={() =>
                onPush({
                  confirmPhrase: confirmText.trim().toUpperCase(),
                  confirmMoney: true,
                  confirmStatus: true,
                  pauseFirst,
                  overrideDrift,
                })
              }
            >
              Update on Google
            </Button>
          </>
        }
      >
        <div className={styles.pushDialog}>
          <p>
            <strong>{autoCount}</strong> change{autoCount === 1 ? '' : 's'} will be applied to the
            live Google campaign now.
            {manualCount > 0 && (
              <>
                {' '}
                <strong>{manualCount}</strong> other change{manualCount === 1 ? '' : 's'} can’t be
                pushed automatically yet — you’ll still see them here to apply in Google.
              </>
            )}
          </p>
          {(diff.hasMoneyImpact || statusChange) && (
            <div className={styles.pushWarn}>
              <strong>This is a real change to a live campaign on Google.</strong>
              <ul>
                {diff.hasMoneyImpact && (
                  <li>
                    It can change spend (budget, bid strategy, keywords, negatives, locations or ad
                    schedule).
                  </li>
                )}
                {statusChange && <li>It changes the campaign’s enabled/paused status.</li>}
              </ul>
            </div>
          )}
          <label className={styles.pushOption}>
            <input
              type="checkbox"
              checked={pauseFirst}
              onChange={(e) => setPauseFirst(e.target.checked)}
            />
            Pause the campaign on Google before applying, and leave it paused
          </label>
          <label className={styles.pushOption}>
            <input
              type="checkbox"
              checked={overrideDrift}
              onChange={(e) => setOverrideDrift(e.target.checked)}
            />
            Push even if Google’s live settings differ from our record (advanced)
          </label>
          <div className={styles.pushConfirm}>
            <label htmlFor="push-confirm-phrase">
              Type <strong>{APPLY_CONFIRM_PHRASE}</strong> to confirm this push to the live
              campaign.
            </label>
            <input
              id="push-confirm-phrase"
              type="text"
              className={styles.pushConfirmInput}
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder={APPLY_CONFIRM_PHRASE}
              autoComplete="off"
              autoCapitalize="characters"
              spellCheck={false}
            />
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ─── Campaign Performance tab ──────────────────────────────────────────────

function InsightCards({ insights }: { insights: CampaignInsight[] }) {
  return (
    <div className={styles.insightsGrid}>
      {insights.map((ins, i) => (
        <div key={i} className={`${styles.insightCard} ${styles[`insightCard_${ins.sentiment}`]}`}>
          {ins.metric && <div className={styles.insightMetric}>{ins.metric}</div>}
          <div className={styles.insightTitle}>{ins.title}</div>
          <div className={styles.insightBody}>{ins.body}</div>
        </div>
      ))}
    </div>
  );
}

function CategoryBarChart({ categories }: { categories: SearchTermCategory[] }) {
  const maxSpend = Math.max(1, ...categories.map((c) => c.spend));
  const isWasteCategory = (name: string) =>
    /irrel|waste|career|educat|school|training|job|hiring|diy|review/i.test(name);

  return (
    <div className={styles.categoryChart}>
      <div className={styles.categoryBars}>
        {categories.map((cat) => {
          const pct = (cat.spend / maxSpend) * 100;
          const waste = isWasteCategory(cat.category);
          return (
            <div key={cat.category} className={styles.categoryRow} title={cat.description}>
              <div className={styles.categoryLabel}>{cat.category}</div>
              <div className={styles.categoryBarTrack}>
                <div
                  className={`${styles.categoryBarFill} ${waste ? styles.categoryBarFill_waste : ''}`}
                  style={{ width: `${Math.max(pct, 1)}%` }}
                />
              </div>
              <div className={styles.categoryStats}>
                <span className={styles.categoryStatSpend}>{formatCurrency(cat.spend)}</span>
                {cat.conversions > 0 && (
                  <span className={styles.categoryStatConv}>
                    {formatConversions(cat.conversions)} conv
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CampaignPerformanceTab({
  campaignId,
  totalSpend,
  totalImpr,
  totalClicks,
  totalConv,
  ctr,
  cpa,
}: {
  campaignId: string;
  totalSpend: number;
  totalImpr: number;
  totalClicks: number;
  totalConv: number;
  ctr: number;
  cpa: number;
}) {
  const searchTermsQuery = useQuery({
    queryKey: campaignKeys.searchTerms(campaignId),
    queryFn: () => campaignsApi.overview.campaignSearchTerms(campaignId),
  });
  const heatmapQuery = useQuery({
    queryKey: campaignKeys.heatmap(campaignId),
    queryFn: () => campaignsApi.overview.campaignHeatmap(campaignId),
  });
  const insightsQuery = useQuery({
    queryKey: campaignKeys.insights(campaignId),
    queryFn: () => campaignsApi.overview.campaignInsights(campaignId),
  });

  const searchTerms = searchTermsQuery.data?.data ?? [];
  const heatmap = heatmapQuery.data?.data;
  const insights = insightsQuery.data?.data;
  const [visibleTerms, setVisibleTerms] = useState(25);
  const [showAllTerms, setShowAllTerms] = useState(false);

  const hasData = totalSpend > 0 || searchTerms.length > 0;

  function daysAgo(iso: string) {
    const ms = Date.now() - new Date(iso).getTime();
    const d = Math.floor(ms / 86_400_000);
    return d === 0 ? 'today' : d === 1 ? '1d ago' : `${d}d ago`;
  }

  return (
    <div className={styles.tabContent}>
      {!hasData ? (
        <div className={styles.emptyState}>
          No performance data yet. Once the campaign is live and ad platforms start reporting,
          metrics will populate here.
        </div>
      ) : (
        <>
          <div className={styles.metricsGrid}>
            <MetricCard title="Spend" value={formatCurrency(totalSpend)} sub="MTD" />
            <MetricCard title="Impressions" value={formatNumber(totalImpr)} />
            <MetricCard
              title="Clicks"
              value={formatNumber(totalClicks)}
              sub={`${formatPercent(ctr)} CTR`}
            />
            <MetricCard
              title="Conversions"
              value={formatConversions(totalConv)}
              sub={`${formatCurrency(cpa)} CPA`}
            />
          </div>
          <AdsTrendChart campaignId={campaignId} />

          {/* AI Insights */}
          <div className={styles.perfSection}>
            <div className={styles.insightsSectionHeader}>
              <div className={styles.perfSectionTitle}>AI Insights</div>
              {insights?.generatedAt && (
                <span className={styles.insightsAge}>
                  Generated {daysAgo(insights.generatedAt)}
                </span>
              )}
            </div>
            {insights && insights.insights.length > 0 ? (
              <InsightCards insights={insights.insights} />
            ) : (
              <div className={styles.insightsPlaceholder}>
                Insights are generated the next time recommendations run.
              </div>
            )}
          </div>

          {/* Search Term Category Breakdown */}
          {insights && insights.categories.length > 0 && (
            <div className={styles.perfSection}>
              <div className={styles.perfSectionTitle}>Search Term Breakdown</div>
              <CategoryBarChart categories={insights.categories} />
            </div>
          )}

          {/* Collapsible raw search terms table */}
          {searchTerms.length > 0 && (
            <div className={styles.perfSection}>
              <button
                type="button"
                className={styles.searchTermsToggle}
                onClick={() => setShowAllTerms((v) => !v)}
              >
                {showAllTerms ? '▲' : '▶'} View all search terms ({searchTerms.length})
              </button>
              {showAllTerms && (
                <>
                  <div className={styles.searchTermsTable}>
                    <div className={styles.searchTermsHeader}>
                      <span>Search Term</span>
                      <span>Impr.</span>
                      <span>Clicks</span>
                      <span>Conv.</span>
                      <span>Cost</span>
                    </div>
                    {searchTerms.slice(0, visibleTerms).map((t) => (
                      <div key={t.term} className={styles.searchTermsRow}>
                        <span className={styles.searchTermName}>{t.term}</span>
                        <span className={styles.searchTermVal}>{formatNumber(t.impressions)}</span>
                        <span className={styles.searchTermVal}>{formatNumber(t.clicks)}</span>
                        <span
                          className={
                            t.conversions > 0 ? styles.searchTermValBold : styles.searchTermVal
                          }
                        >
                          {formatConversions(t.conversions)}
                        </span>
                        <span className={styles.searchTermVal}>{formatCurrency(t.cost)}</span>
                      </div>
                    ))}
                  </div>
                  {visibleTerms < searchTerms.length && (
                    <div className={styles.loadMore}>
                      <Button variant="secondary" onClick={() => setVisibleTerms((v) => v + 25)}>
                        Load more
                      </Button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {heatmap && heatmap.cells.length > 0 && (
            <div className={styles.perfSection}>
              <div className={styles.perfSectionTitle}>Day &amp; Hour Heatmap</div>
              <DayHourHeatmap cells={heatmap.cells} aiInsight={heatmap.aiInsight} />
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ─── Campaign Recommendations tab ──────────────────────────────────────────

type RecFilter = 'pending' | 'approved' | 'dismissed';

function CampaignRecsTab({ campaignId }: { campaignId: string }) {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [filter, setFilter] = useState<RecFilter>('pending');

  const recsQuery = useQuery({
    queryKey: campaignKeys.recs(campaignId),
    queryFn: () => campaignsApi.getCampaignRecs(campaignId),
    // Poll every 3s while any rec is executing, so the UI updates through
    // APPROVED → EXECUTING → DONE/FAILED
    refetchInterval: (query) => {
      const recs = query.state.data?.data ?? [];
      const hasExecuting = recs.some(
        (r: { status: string }) => r.status === 'approved' || r.status === 'executing',
      );
      return hasExecuting ? 3000 : false;
    },
  });

  const approveMut = useMutation({
    mutationFn: (recId: string) => campaignsApi.approveCampaignRec(campaignId, recId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.recs(campaignId) });
      queryClient.invalidateQueries({ queryKey: campaignKeys.locationRecs() });
      showToast('Recommendation approved & executing', 'success');
    },
  });

  const dismissMut = useMutation({
    mutationFn: (recId: string) => campaignsApi.dismissCampaignRec(campaignId, recId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: campaignKeys.recs(campaignId) });
      queryClient.invalidateQueries({ queryKey: campaignKeys.locationRecs() });
    },
  });

  const allRecs = recsQuery.data?.data ?? [];
  const counts: Record<RecFilter, number> = {
    pending: allRecs.filter((r) => r.status === 'pending').length,
    approved: allRecs.filter((r) => ['approved', 'executing', 'done', 'failed'].includes(r.status))
      .length,
    dismissed: allRecs.filter((r) => r.status === 'dismissed').length,
  };
  const filtered = allRecs.filter((r) => {
    if (filter === 'approved') {
      return ['approved', 'executing', 'done', 'failed'].includes(r.status);
    }
    return r.status === filter;
  });

  return (
    <div className={styles.tabContent}>
      {recsQuery.isLoading ? (
        <div className={styles.emptyState}>Loading…</div>
      ) : allRecs.length === 0 ? (
        <div className={styles.emptyState}>
          Per-campaign recommendations will appear here once the campaign has been live for at least
          a few days.
        </div>
      ) : (
        <>
          <div className={styles.recFilters}>
            {(['pending', 'approved', 'dismissed'] as RecFilter[]).map((f) => (
              <button
                key={f}
                className={`${styles.recFilterBadge} ${filter === f ? styles.recFilterBadgeActive : ''}`}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
                <span className={styles.recFilterCount}>{counts[f]}</span>
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <div className={styles.emptyState}>No {filter} recommendations.</div>
          ) : (
            filtered.map((rec) => (
              <div
                key={rec.id}
                className={`${styles.recCard} ${rec.status === 'dismissed' ? styles.recCardDismissed : ''}`}
              >
                <div className={styles.recTitle}>{rec.title}</div>
                <div className={styles.recDesc}>{rec.description}</div>
                {rec.impact && (
                  <div className={styles.recDesc}>
                    <strong>Impact:</strong> {rec.impact}
                  </div>
                )}
                {rec.status === 'pending' && (
                  <AutopilotGuard>
                    <div className={styles.recActions}>
                      <Button
                        size="sm"
                        onClick={() => approveMut.mutate(rec.id)}
                        isLoading={approveMut.isPending && approveMut.variables === rec.id}
                      >
                        {rec.actionLabel ?? 'Approve'}
                      </Button>
                      {rec.editLabel && (
                        <Button variant="secondary" size="sm">
                          {rec.editLabel}
                        </Button>
                      )}
                      <Button
                        variant="tertiary"
                        size="sm"
                        onClick={() => dismissMut.mutate(rec.id)}
                        isLoading={dismissMut.isPending && dismissMut.variables === rec.id}
                      >
                        Dismiss
                      </Button>
                    </div>
                  </AutopilotGuard>
                )}
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
}

// ─── Inline card (click-to-edit field in a card) ─────────────────────────────

function InlineCard({
  label,
  value,
  onSave,
  multiline = false,
  placeholder,
  readOnly = false,
}: {
  label: string;
  value?: string;
  onSave: (value: string) => void;
  multiline?: boolean;
  placeholder?: string;
  readOnly?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

  useEffect(() => {
    setDraft(value ?? '');
  }, [value]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      if (multiline && inputRef.current instanceof HTMLTextAreaElement) {
        inputRef.current.style.height = 'auto';
        inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
      }
    }
  }, [editing, multiline]);

  const handleBlur = () => {
    setEditing(false);
    if (draft !== (value ?? '')) {
      onSave(draft);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setDraft(value ?? '');
      setEditing(false);
    }
    if (!multiline && e.key === 'Enter') {
      (e.target as HTMLElement).blur();
    }
  };

  return (
    <div className={styles.fieldCard}>
      <span className={styles.fieldLabel}>{label}</span>
      {editing ? (
        multiline ? (
          <textarea
            ref={inputRef as React.RefObject<HTMLTextAreaElement>}
            className={styles.fieldInput}
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height = e.target.scrollHeight + 'px';
            }}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            rows={2}
          />
        ) : (
          <input
            ref={inputRef as React.RefObject<HTMLInputElement>}
            className={styles.fieldInput}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
          />
        )
      ) : (
        <span
          className={readOnly ? styles.fieldValueReadOnly : styles.fieldValueClick}
          onClick={readOnly ? undefined : () => setEditing(true)}
          role={readOnly ? undefined : 'button'}
          tabIndex={readOnly ? undefined : 0}
          onKeyDown={readOnly ? undefined : (e) => e.key === 'Enter' && setEditing(true)}
        >
          {value || (
            <span className={styles.fieldPlaceholder}>
              {readOnly ? '—' : (placeholder ?? 'Click to edit')}
            </span>
          )}
        </span>
      )}
    </div>
  );
}
